import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Form, Tab, Tabs } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { updateProperties } from '../../slices/inspectorSlice';
import ScriptEditor from '../common/ScriptEditor';
import { BdaServiceApplicationProperty, BdaServiceProperty, BdaServiceResourceProperty } from '../../models/BdaService';
import InputTokenizer from '../generics/InputTokenizer';
import { Avatar, Flex, Input, Select, Slider, Tooltip } from 'antd';
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import classNames from 'classnames';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { setObjectType } from '../../utils';

type Props = {
  properties: BdaServiceProperty[];
  agentMode?: boolean;
  onAgentFieldChange?: (field: string, value: string) => void;
}

const sliderFormatter = (prop: BdaServiceResourceProperty) => (value?: number) => `${value}${prop.metricUnit}`;

export default function ServicePropertiesForm({ properties, agentMode, onAgentFieldChange }: Props) {
  const dispatch = useDispatch();
  const [editingProperties, setEditingProperties] = useState(properties);
  const environmentVariables = useSelector((state: RootState) => state.designer.environmentVariables);
  const variablesOptions = useMemo(() => environmentVariables?.map(env => ({ label: env.name, value: env.id, image: env.img })), [environmentVariables]);

  const envVarLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  useEffect(() => {
    setEditingProperties(properties);
  }, [properties]);

  const updateServiceProperties = useCallback((_evt?: any, newProperties?: BdaServiceProperty[]) => {
    dispatch(updateProperties(newProperties || editingProperties));
  }, [dispatch, editingProperties]);

  const handlePropChange = useCallback((key: string, checkbox?: boolean, pure?: boolean) => (evt: any) => {
    setEditingProperties(curr => {
      const value = pure ? evt : checkbox ? evt.target.checked : evt.target.value;
      const index = curr.findIndex(prop => prop.key === key);
      const newProperties = cloneDeep(curr);
      newProperties[index].value = value;
      if (checkbox) updateServiceProperties(null, newProperties);
      return newProperties;
    });
  }, [updateServiceProperties]);

  const handleBlur = useCallback((prop: BdaServiceProperty) => () => {
    if (agentMode && [window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY, window.__env.DESIGNER__AGENT_PROMPT_PROPERTY].includes(prop.key)) onAgentFieldChange?.(prop.key, prop.value || "");
    updateServiceProperties();
  }, [agentMode, onAgentFieldChange, updateServiceProperties]);

  const toggleInterpolation = useCallback((id: number) => () => {
    setEditingProperties(curr => {
      const index = curr.findIndex(prop => prop.id === id);
      const newProperties = cloneDeep(curr);
      newProperties[index].interpolationEnabled = !newProperties[index].interpolationEnabled;
      updateServiceProperties(null, newProperties);
      return newProperties;
    });
  }, [updateServiceProperties]);

  const tabs = useMemo(() => {
    return editingProperties
      .filter(prop => !prop.invisible)
      .reduce((acc, curr) => {
        const type = curr.type;
        if (acc[type]) acc[type].push(curr);
        else acc[type] = [curr];
        return acc;
      }, {} as Record<string, BdaServiceProperty[]>);
  }, [editingProperties]);

  const defaultTab = useMemo(() => {
    return tabs['application']?.length ? 'application' : 'static';
  }, [tabs]);

  const renderAgentLlmModelConfig = useCallback((prop: BdaServiceProperty) => {
    return (
      <Form.Group controlId={`${prop.key}-form-group`} className="mb-3" key={prop.key}>
        <Form.Label>{prop.key}</Form.Label>
        <Select
          className="w-100 h-auto"
          value={prop.value}
          onChange={handlePropChange(prop.key!, false, true)}
          onBlur={handleBlur(prop)}
          placeholder={`${prop.key} value`}
          allowClear
        >
          {variablesOptions?.map(option => (
            <Select.Option key={option.value} label={option.label} value={option.value}>
              <Avatar
                src={option.image}
                icon={<FontAwesomeIcon icon={solid('image')} size="sm" />}
                className="border me-2"
                style={{ height: 25, width: 25 }}
              />
              <span>{option.label}</span>
            </Select.Option>
          ))}
        </Select>
      </Form.Group>
    )
  }, [handleBlur, handlePropChange, variablesOptions]);

  const renderPropertyValue = useCallback((prop: BdaServiceProperty) => {
    if (prop.interpolationEnabled) return (
      <InputTokenizer
        placeholder={prop.defaultValue || `${prop.key} value`}
        value={prop.value || ""}
        onChange={handlePropChange(prop.key!, false, true)}
        onBlur={handleBlur(prop)}
        variables={environmentVariables}
      />
    )

    if (prop.key === window.__env.DESIGNER__AGENT_PROMPT_PROPERTY) return (
      <Input.TextArea
        value={prop.value || ""}
        placeholder={prop.defaultValue || `${prop.key} value`}
        onChange={handlePropChange(prop.key!)}
        onBlur={handleBlur(prop)}
      />
    )

    if (prop.type === 'resource' && setObjectType<BdaServiceResourceProperty>(prop)) return (
      <div className="px-2">
        <Slider
          step={prop.step}
          value={prop.value}
          defaultValue={prop.defaultValue}
          min={prop.allowedRange?.min}
          max={prop.allowedRange?.max}
          marks={{
            [prop.allowedRange?.min || '0']: <span>{prop.allowedRange?.min || '0'}{prop.metricUnit}</span>,
            [prop.allowedRange?.max || '100']: <span>{prop.allowedRange?.max || '100'}{prop.metricUnit}</span>
          }}
          tooltip={{ formatter: sliderFormatter(prop) }}
          onChange={handlePropChange(prop.key!, false, true)}
          onChangeComplete={updateServiceProperties}
        />
      </div>
    )

    return prop.valueType === "JSON"
      ? (
        <ScriptEditor
          height={150}
          value={prop.value || prop.defaultValue || ""}
          onChange={handlePropChange(prop.key!, false, true)}
          onBlur={updateServiceProperties}
        />
      )
      : (prop as BdaServiceApplicationProperty).obfuscated
        ? (
          <Input.Password
            value={prop.value || ""}
            placeholder={prop.defaultValue || `${prop.key} value`}
            onChange={handlePropChange(prop.key!)}
            onBlur={handleBlur(prop)}
          />
        )
        : (
          <Form.Control
            size="sm"
            type={prop.valueType === 'INT' ? 'number' : 'text'}
            placeholder={prop.defaultValue || `${prop.key} value`}
            value={prop.value || ""}
            onChange={handlePropChange(prop.key!)}
            onBlur={handleBlur(prop)}
          />
        )
  }, [environmentVariables, handleBlur, handlePropChange, updateServiceProperties]);

  const renderInputGroupContent = useCallback((prop: BdaServiceProperty) => {
    if (prop.valueType === "BOOLEAN") {
      const value = typeof prop.value === "boolean" ? prop.value : prop.value === "true";
      const defaultChecked = typeof prop.defaultValue === "boolean" ? prop.defaultValue : prop.defaultValue === "true";
      const checked = typeof prop.value === 'boolean' ? value : defaultChecked;

      return (
        <>
          <Form.Check
            type="checkbox"
            label={prop.key}
            id={`${prop.key}-checkbox`}
            checked={checked}
            onChange={handlePropChange(prop.key!, true)}
            className="mb-0"
          />
          <Form.Text>{prop.description}</Form.Text>
        </>
      )
    }

    return (
      <>
        <Flex align="center" justify="space-between" className="overflow-hidden">
          <Tooltip mouseEnterDelay={0.5} title={`${prop.key} ${prop.mandatory ? '*' : ''}`}>
            <Form.Label className="text-truncate">{prop.key} {prop.mandatory && '*'}</Form.Label>
          </Tooltip>
          <IconButton
            tooltip={{
              title: (
                <>
                  <b className="d-block">Toggle Variable Interpolation</b>
                  <span>{envVarLabels.plural} can be interpolated with <Badge bg="dark" className="p-1">{"{{ variableName }}"}</Badge></span>
                </>
              ),
              placement: "left",
              mouseEnterDelay: 0.5,
            }}
            className={classNames("interpolation-button", { enabled: prop.interpolationEnabled })}
            onClick={toggleInterpolation(prop.id!)}
          >
            <FontAwesomeIcon icon={solid('code')} />
          </IconButton>
        </Flex>
        {renderPropertyValue(prop)}
        <Form.Text>{prop.description}</Form.Text>
      </>
    );
  }, [handlePropChange, toggleInterpolation, renderPropertyValue, envVarLabels]);

  return (
    <Tabs defaultActiveKey={defaultTab}>
      {Object.entries(tabs)
        .sort()
        .map(([tab, properties]) => (
          <Tab key={tab} title={tab} eventKey={tab}>
            <div className="p-2">
              {properties
                .sort((a, b) => {
                  if (a.mandatory && !b.mandatory) return -1;
                  else if (b.mandatory) return 0;
                  else return 1;
                })
                .map(prop => prop.key === window.__env.DESIGNER__AGENT_LLM_MODEL_PROPERTY
                  ? renderAgentLlmModelConfig(prop)
                  : (
                    <Form.Group
                      key={prop.key}
                      controlId={prop.valueType !== 'BOOLEAN' ? `${prop.key}-form-control` : undefined}
                      className="mb-3"
                    >
                      {renderInputGroupContent(prop)}
                    </Form.Group>
                  ))}
            </div>
          </Tab>
        ))}
    </Tabs>
  )
}
